import type { FC } from 'react';
import { useMenu } from '@components/menu';
import { Show } from '@components/Show';
import Tooltip from '@components/Tooltip';
import { NavAttachmentIcon, sidebarBlockTextClasses } from '@ContractBuilder/components/shared-sidebar';
import { useMultiSelectStore } from '@ContractBuilder/store/multi-select.store';
import type { Attachment } from '@ContractBuilder/types';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import { BlockCheckboxController, CheckboxSpacer } from '@features/multi-select-blocks';
import { SwitchVerticalIcon as DragAndDropIcon } from '@heroicons/react/outline';
import type { ResourceBlock } from '@root/@types/types';
import clsx from 'clsx';

import { InlineAttachmentMenu } from '../components/InlineAttachmentMenu';

const visibleOnHoverClasses = ['group-hover:opacity-100', 'opacity-0'];

interface SortedInlineAttachmentItemProps {
  attachment: Attachment;
  block: ResourceBlock;
  listeners?: DraggableSyntheticListeners;
  attributes?: DraggableAttributes;
  isDragDisabled?: boolean;
  shouldDisableOperations?: boolean;
}

export const SortedBlockInlineAttachment: FC<SortedInlineAttachmentItemProps> = ({
  attachment,
  block,
  listeners,
  attributes,
  isDragDisabled = false,
  shouldDisableOperations = false,
}) => {
  const { isOpen: isMenuOpen, onClose, onOpen } = useMenu();
  const isAnyMultiSelectActive = useMultiSelectStore(
    (state) => state.activePartId !== null && state.selectedBlockIds.size > 0,
  );
  const scrollableId = `${block.id}-navigation`;
  const canShowCheckbox = !shouldDisableOperations;

  return (
    <div className="flex w-full flex-nowrap items-center justify-between" id={scrollableId}>
      <div className="flex w-full flex-nowrap items-center gap-1.5 truncate">
        {canShowCheckbox ? (
          <BlockCheckboxController partId={block.section_id} blockId={block.id} blockName={attachment.name} />
        ) : (
          <CheckboxSpacer />
        )}
        <NavAttachmentIcon />

        <Tooltip content={attachment.name}>
          <p className={sidebarBlockTextClasses({ bold: isMenuOpen })}>{attachment.name}</p>
        </Tooltip>
      </div>
      <Show when={!shouldDisableOperations}>
        <div className={clsx('flex items-center', isAnyMultiSelectActive && 'pointer-events-none opacity-30')}>
          <Show when={(!isDragDisabled && !!listeners) || isAnyMultiSelectActive}>
            <div
              data-testid={`${attachment.name}-draggable-block-trigger`}
              className={clsx(
                visibleOnHoverClasses,
                'group-hover:text-info-800 pl-1',
                !isAnyMultiSelectActive && 'cursor-grab active:cursor-grabbing',
                isMenuOpen ? 'text-info-800 opacity-100' : 'text-info-400',
              )}
            >
              <div className="relative h-6 w-6" {...(isAnyMultiSelectActive ? {} : { ...listeners, ...attributes })}>
                <DragAndDropIcon className="h-6 w-6" />
              </div>
            </div>
          </Show>
          <InlineAttachmentMenu
            attachment={attachment}
            className={clsx(
              visibleOnHoverClasses,
              'group-hover:text-info-800 cursor-pointer',
              isMenuOpen ? 'text-info-800 opacity-100' : 'text-info-400',
            )}
            isOpen={isMenuOpen}
            onClose={onClose}
            onOpen={onOpen}
          />
        </div>
      </Show>
    </div>
  );
};
